import { Button, Typography } from '@breetometer/components';
import { NotificationBannerAlertIcon, NotificationBannerCloseIcon } from 'BreetAssets';
import { generateUEID } from 'BreetHelpers';
import { useCallback, useEffect, useState } from 'react';

import type { IMarqueeNotification } from '../NotificationBanner.helpers';

const MarqueNotification = ({ data }: IMarqueeNotification) => {
	const now = new Date();
	const localStorageKey = `flashMessageDismissed_${data?.severity}`;

	const [isHover, setIsHover] = useState(false);
	const [isClicked, setIsClicked] = useState(false);
	const [isVisible, setIsVisible] = useState(() => {
		const dismissed = localStorage.getItem(localStorageKey);
		return !dismissed && !!data?.isVip;
	});

	const handleMouseEnter = useCallback(() => setIsHover(true), []);
	const handleMouseLeave = useCallback(() => setIsHover(false), []);
	const handleClick = useCallback(() => {
		setIsHover((prev) => !prev);
		setIsClicked((prev) => !prev);
	}, []);

	const isWithinTimeRange = data?.isActive && new Date(data.createdAt ?? '') <= now && now <= new Date(data.expiresAt);

	useEffect(() => {
		setIsVisible(!!(data?.isVip && isWithinTimeRange));
	}, [data?.isVip, isWithinTimeRange]);

	const handleClose = useCallback(() => {
		setIsVisible(false);
		localStorage.setItem(localStorageKey, 'true');
	}, [localStorageKey]);

	const status = data?.severity === 'soft' ? 'success' : 'error';

	const bgstatusClass = status === 'success' ? 'bg-success-800' : 'bg-dark-maroon';

	const statusClass = status === 'success' ? 'color-success-400' : 'color-light-coral-red';

	const wrapperClass = `notificationbanner ${status} ${bgstatusClass} button-cancel-transform`;

	const marqueeContent = `marquee_content${isHover || isClicked ? ' paused' : ' running'}`;

	if (!isVisible || !isWithinTimeRange || !data.isVip || !data.message) {
		return null;
	}

	return (
		<Typography
			as='span'
			role='button'
			tabIndex={0}
			type='button'
			className={wrapperClass}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			data-testid='notification-banner'
		>
			<NotificationBannerAlertIcon status={status} />

			<div className='marquee'>
				<Button
					variant='native'
					className={marqueeContent}
					onClick={handleClick}
				>
					{Array(6)
						.fill(null)
						.map(() => (
							<Typography
								as='span'
								key={generateUEID()}
								className={`${status} ${statusClass}`}
							>
								{data.message}
							</Typography>
						))}
				</Button>
			</div>

			{data.severity === 'soft' && (
				<NotificationBannerCloseIcon
					status={status}
					className='notificationbannercloseicon cursor_pointer'
					focusable
					onClick={handleClose}
				/>
			)}
		</Typography>
	);
};

export default MarqueNotification;
